import L from "leaflet";
import { createNoaaCoopsClient } from "./providers/noaa-coops.js";

const STATE_KEY = "hydrotools:tides:v2";
const FT_PER_M = 3.280839895;
const WINDOW_HOURS = 48;

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, Number(n) || 0));
}

function asNum(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeRange(startIso, endIso) {
  const dayMs = 24 * 3600 * 1000;
  const now = Date.now();
  let startMs = new Date(startIso).getTime();
  let endMs = new Date(endIso).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    startMs = now - (3 * dayMs);
    endMs = now + (4 * dayMs);
  }
  const spanHours = (endMs - startMs) / (3600 * 1000);
  if (spanHours <= WINDOW_HOURS + 1) {
    const mid = startMs + ((endMs - startMs) / 2);
    startMs = mid - (3 * dayMs);
    endMs = mid + (4 * dayMs);
  }
  return {
    startIso: new Date(startMs).toISOString(),
    endIso: new Date(endMs).toISOString(),
  };
}

function readState() {
  const now = Date.now();
  const dayMs = 24 * 3600 * 1000;
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    const range = normalizeRange(
      typeof x.startIso === "string" ? x.startIso : new Date(now - (3 * dayMs)).toISOString(),
      typeof x.endIso === "string" ? x.endIso : new Date(now + (4 * dayMs)).toISOString(),
    );
    return {
      lat: asNum(x.lat, 37.8063),
      lon: asNum(x.lon, -122.4659),
      stationId: typeof x.stationId === "string" ? x.stationId : "9414290",
      stationName: typeof x.stationName === "string" ? x.stationName : "San Francisco",
      datum: String(x.datum || "MLLW"),
      units: x.units === "english" ? "english" : "metric",
      startIso: range.startIso,
      endIso: range.endIso,
      autoPickMode: !!x.autoPickMode,
      windowOffsetHours: Math.max(0, asNum(x.windowOffsetHours, 0)),
      bottomTab: typeof x.bottomTab === "string" ? x.bottomTab : "graph",
    };
  } catch {
    return {
      lat: 37.8063,
      lon: -122.4659,
      stationId: "9414290",
      stationName: "San Francisco",
      datum: "MLLW",
      units: "metric",
      startIso: new Date(now - (3 * dayMs)).toISOString(),
      endIso: new Date(now + (4 * dayMs)).toISOString(),
      autoPickMode: false,
      windowOffsetHours: 0,
      bottomTab: "graph",
    };
  }
}

function saveState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

function toLocalInputValue(ms) {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

function parseLocalInputToIso(v, fallbackIso) {
  const d = new Date(String(v || ""));
  if (!Number.isFinite(d.getTime())) return fallbackIso;
  return d.toISOString();
}

function fmtTime(ms) {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return "--";
  return d.toISOString().replace(".000Z", "Z");
}

function formatValue(v, units) {
  if (!Number.isFinite(v)) return "--";
  if (units === "english") return `${(v * FT_PER_M).toFixed(2)} ft`;
  return `${v.toFixed(3)} m`;
}

function formatDistanceKm(km) {
  if (!Number.isFinite(km)) return "--";
  const nm = km / 1.852;
  return `${nm.toFixed(1)} nm`;
}

function buildPath(series, bounds) {
  const valid = series.filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v));
  if (!valid.length) return "";
  const { minT, maxT, minV, maxV, width, height, pad, plotTop, plotBottom } = bounds;
  const spanT = Math.max(1, maxT - minT);
  const spanV = Math.max(0.001, maxV - minV);
  return valid.map((p, i) => {
    const x = pad + ((p.t - minT) / spanT) * (width - pad * 2);
    const y = (height - plotBottom) - ((p.v - minV) / spanV) * (height - plotTop - plotBottom);
    return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
}

function timeToX(t, bounds) {
  const spanT = Math.max(1, bounds.maxT - bounds.minT);
  return bounds.pad + ((t - bounds.minT) / spanT) * (bounds.width - bounds.pad * 2);
}

function valueToY(v, bounds) {
  const spanV = Math.max(0.001, bounds.maxV - bounds.minV);
  return (bounds.height - bounds.plotBottom) - ((v - bounds.minV) / spanV) * (bounds.height - bounds.plotTop - bounds.plotBottom);
}

function nearestPoint(series, targetMs) {
  if (!Array.isArray(series) || !series.length) return null;
  let best = null;
  let bestDelta = Number.POSITIVE_INFINITY;
  series.forEach((p) => {
    if (!Number.isFinite(p?.t) || !Number.isFinite(p?.v)) return;
    const delta = Math.abs(p.t - targetMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = p;
    }
  });
  return best;
}

function dayBandAndGuideSvg(bounds) {
  const dayMs = 24 * 3600 * 1000;
  const milestoneHours = [0, 6, 12, 18];
  const startDay = Math.floor(bounds.minT / dayMs) * dayMs;
  const endDay = Math.ceil(bounds.maxT / dayMs) * dayMs;
  const bands = [];
  const lines = [];
  const labels = [];
  const dayLabels = [];
  let dayIdx = 0;

  for (let day = startDay; day < endDay; day += dayMs) {
    const dayStart = Math.max(day, bounds.minT);
    const dayEnd = Math.min(day + dayMs, bounds.maxT);
    if (dayEnd <= dayStart) continue;
    const x1 = timeToX(dayStart, bounds);
    const x2 = timeToX(dayEnd, bounds);
    const w = Math.max(0, x2 - x1);
    const shade = dayIdx % 2 === 0 ? "rgba(56,189,248,0.06)" : "rgba(56,189,248,0.02)";
    bands.push(`<rect x="${x1.toFixed(2)}" y="${bounds.plotTop}" width="${w.toFixed(2)}" height="${(bounds.height - bounds.plotTop - bounds.plotBottom).toFixed(2)}" fill="${shade}" />`);

    milestoneHours.forEach((h) => {
      const t = day + (h * 3600 * 1000);
      if (t < bounds.minT || t > bounds.maxT) return;
      const x = timeToX(t, bounds);
      const strong = h === 0 || h === 12;
      lines.push(`<line x1="${x.toFixed(2)}" y1="${bounds.plotTop}" x2="${x.toFixed(2)}" y2="${(bounds.height - bounds.plotBottom).toFixed(2)}" stroke="${strong ? "rgba(148,163,184,0.45)" : "rgba(148,163,184,0.2)"}" stroke-width="${strong ? 1.2 : 1}" />`);
      if (h !== 0) {
        const hh = String(h).padStart(2, "0");
        labels.push(`<text x="${(x + 2).toFixed(2)}" y="${(bounds.height - bounds.plotBottom + 12).toFixed(2)}" fill="rgba(148,163,184,0.82)" font-size="10">${hh}:00Z</text>`);
      }
    });

    // Center labels over each visible day segment; recomputed each frame so left edge follows slider.
    const dayCenter = dayStart + ((dayEnd - dayStart) / 2);
    const dayLabelX = timeToX(dayCenter, bounds);
    const dayLabel = new Date(day).toLocaleDateString("en-US", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "2-digit",
    });
    dayLabels.push(`<text x="${dayLabelX.toFixed(2)}" y="${(bounds.plotTop - 7).toFixed(2)}" text-anchor="middle" fill="rgba(226,232,240,0.92)" font-size="11" font-weight="600">${dayLabel} UTC</text>`);
    dayIdx += 1;
  }

  return {
    bands: bands.join(""),
    lines: lines.join(""),
    dayLabels: dayLabels.join(""),
    labels: labels.join(""),
  };
}

function renderSeriesSvg(observed, predicted, hoverX = null) {
  const width = 780;
  const height = 194;
  const pad = 20;
  const plotTop = 30;
  const plotBottom = 20;
  const all = [...observed, ...predicted].filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v));
  if (!all.length) {
    return { html: `<div class="small">No series available for this range.</div>`, hover: null };
  }
  const minT = Math.min(...all.map((p) => p.t));
  const maxT = Math.max(...all.map((p) => p.t));
  const minRaw = Math.min(...all.map((p) => p.v));
  const maxRaw = Math.max(...all.map((p) => p.v));
  const padV = Math.max(0.1, (maxRaw - minRaw) * 0.08);
  const bounds = {
    minT,
    maxT,
    minV: minRaw - padV,
    maxV: maxRaw + padV,
    width,
    height,
    pad,
    plotTop,
    plotBottom,
  };
  const guides = dayBandAndGuideSvg(bounds);

  const obsPath = buildPath(observed, bounds);
  const predPath = buildPath(predicted, bounds);
  const x1 = pad;
  const x2 = width - pad;
  const yMid = plotTop + ((height - plotTop - plotBottom) / 2);
  let hover = null;
  let hoverSvg = "";
  if (Number.isFinite(hoverX)) {
    const clampedX = clamp(hoverX, x1, x2);
    const spanT = Math.max(1, maxT - minT);
    const targetMs = minT + ((clampedX - x1) / (x2 - x1)) * spanT;
    const obsPt = nearestPoint(observed, targetMs);
    const predPt = nearestPoint(predicted, targetMs);
    hover = { t: targetMs, observed: obsPt, predicted: predPt };

    hoverSvg += `<line x1="${clampedX.toFixed(2)}" y1="${plotTop}" x2="${clampedX.toFixed(2)}" y2="${(height - plotBottom).toFixed(2)}" stroke="rgba(255,255,255,0.4)" stroke-width="1" />`;
    if (obsPt) {
      const ox = timeToX(obsPt.t, bounds);
      const oy = valueToY(obsPt.v, bounds);
      hoverSvg += `<circle cx="${ox.toFixed(2)}" cy="${oy.toFixed(2)}" r="3.8" fill="#f97316" stroke="rgba(0,0,0,0.35)" stroke-width="1" />`;
    }
    if (predPt) {
      const px = timeToX(predPt.t, bounds);
      const py = valueToY(predPt.v, bounds);
      hoverSvg += `<circle cx="${px.toFixed(2)}" cy="${py.toFixed(2)}" r="3.8" fill="#60a5fa" stroke="rgba(0,0,0,0.35)" stroke-width="1" />`;
    }
  }

  const html = `
    <div id="tides-chart-wrap">
      <svg id="tides-chart-svg" viewBox="0 0 ${width} ${height}" style="width:100%; height:auto; display:block; border:1px solid rgba(148,163,184,0.3); border-radius:8px; background:rgba(2,6,23,0.2);">
        ${guides.bands}
        ${guides.lines}
        ${guides.dayLabels}
        <line x1="${x1}" y1="${yMid}" x2="${x2}" y2="${yMid}" stroke="rgba(148,163,184,0.25)" stroke-width="1" />
        ${predPath ? `<path d="${predPath}" fill="none" stroke="#60a5fa" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />` : ""}
        ${obsPath ? `<path d="${obsPath}" fill="none" stroke="#f97316" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />` : ""}
        ${hoverSvg}
        ${guides.labels}
      </svg>
    </div>
    <div class="small" style="margin-top:4px;">
      <span style="display:inline-block; width:10px; height:10px; background:#f97316; border-radius:999px; margin-right:4px;"></span>Observed
      <span style="display:inline-block; width:10px; height:10px; background:#60a5fa; border-radius:999px; margin:0 4px 0 12px;"></span>Predicted
    </div>
  `;

  return { html, hover, bounds };
}

function requestedBoundsMs(state) {
  const a = new Date(state.startIso).getTime();
  const b = new Date(state.endIso).getTime();
  const now = Date.now();
  const min = Number.isFinite(a) ? a : now - (24 * 3600 * 1000);
  const max = Number.isFinite(b) ? b : now + (24 * 3600 * 1000);
  return max >= min ? { minT: min, maxT: max } : { minT: max, maxT: min };
}

function windowParams(state) {
  const hourMs = 3600 * 1000;
  const windowMs = WINDOW_HOURS * hourMs;
  const { minT, maxT } = requestedBoundsMs(state);
  const maxOffsetHours = Math.max(0, Math.floor((maxT - minT - windowMs) / hourMs));
  const offsetHours = clamp(state.windowOffsetHours, 0, maxOffsetHours);
  const startMs = minT + offsetHours * hourMs;
  const endMs = Math.min(maxT, startMs + windowMs);
  return { minT, maxT, maxOffsetHours, offsetHours, startMs, endMs };
}

function filterSeriesByWindow(rows, startMs, endMs) {
  return (rows || []).filter((p) => Number.isFinite(p?.t) && p.t >= startMs && p.t <= endMs);
}

export const tidesTool = {
  id: "tides",
  name: "Tides",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  const noaa = createNoaaCoopsClient();
  let containerEl = null;
  let state = readState();
  let loading = false;
  let status = "";
  let series = { observed: [], predicted: [], hilo: [] };
  let hoverX = null;
  let scrubDragging = false;
  let nearestInfo = { distanceKm: null };
  let stationMarker = null;
  let pickMarker = null;
  let mapClickOff = null;

  function persist() {
    saveState(state);
  }

  function setStatus(text) {
    status = String(text || "");
    render();
  }

  function parseForm() {
    if (!containerEl) return;
    state.lat = clamp(containerEl.querySelector("#tides-lat")?.value, -90, 90);
    state.lon = clamp(containerEl.querySelector("#tides-lon")?.value, -180, 180);
    state.datum = String(containerEl.querySelector("#tides-datum")?.value || "MLLW");
    state.units = containerEl.querySelector("#tides-units")?.value === "english" ? "english" : "metric";
    const rawStart = parseLocalInputToIso(containerEl.querySelector("#tides-start")?.value, state.startIso);
    const rawEnd = parseLocalInputToIso(containerEl.querySelector("#tides-end")?.value, state.endIso);
    const range = normalizeRange(rawStart, rawEnd);
    state.startIso = range.startIso;
    state.endIso = range.endIso;
    persist();
  }

  function updateMapMarkers() {
    if (!app?.map?.instance) return;
    const map = app.map.instance;
    const hasStation = Number.isFinite(state.stationLat) && Number.isFinite(state.stationLon);
    if (hasStation) {
      if (!stationMarker) {
        stationMarker = L.circleMarker([state.stationLat, state.stationLon], {
          radius: 7,
          color: "#60a5fa",
          weight: 2,
          fillColor: "#60a5fa",
          fillOpacity: 0.25,
        });
      } else {
        stationMarker.setLatLng([state.stationLat, state.stationLon]);
      }
      if (!map.hasLayer(stationMarker)) stationMarker.addTo(map);
      stationMarker.bindTooltip(`Tide station ${state.stationId}`, { direction: "top", opacity: 0.9 });
    } else if (stationMarker && map.hasLayer(stationMarker)) {
      map.removeLayer(stationMarker);
    }

    if (Number.isFinite(state.lat) && Number.isFinite(state.lon)) {
      if (!pickMarker) {
        pickMarker = L.circleMarker([state.lat, state.lon], {
          radius: 5,
          color: "#f97316",
          weight: 2,
          fillColor: "#f97316",
          fillOpacity: 0.2,
        });
      } else {
        pickMarker.setLatLng([state.lat, state.lon]);
      }
      if (!map.hasLayer(pickMarker)) pickMarker.addTo(map);
      pickMarker.bindTooltip("Pick point", { direction: "right", opacity: 0.9 });
    }
  }

  async function resolveNearestStation() {
    parseForm();
    loading = true;
    setStatus("Finding nearest NOAA station...");
    try {
      const hit = await noaa.findNearestStation(state.lat, state.lon);
      if (!hit?.station) throw new Error("No station found");
      state.stationId = hit.station.id;
      state.stationName = hit.station.name;
      state.stationLat = hit.station.lat;
      state.stationLon = hit.station.lon;
      nearestInfo = { distanceKm: hit.distanceKm };
      persist();
      updateMapMarkers();
      setStatus(`Selected ${hit.station.name} (${hit.station.id})`);
    } catch (err) {
      console.error("[Tides] nearest station failed", err);
      setStatus(`Nearest station lookup failed: ${err?.message || String(err)}`);
    } finally {
      loading = false;
      render();
    }
  }

  async function loadSeries() {
    parseForm();
    if (!state.stationId) {
      setStatus("Select station first.");
      return;
    }
    const startMs = new Date(state.startIso).getTime();
    const endMs = new Date(state.endIso).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
      setStatus("Invalid time range.");
      return;
    }
    loading = true;
    setStatus(`Loading tides for station ${state.stationId}...`);
    try {
      series = await noaa.fetchSeries({
        stationId: state.stationId,
        datum: state.datum,
        units: state.units,
        start: state.startIso,
        end: state.endIso,
      });
      const win = windowParams(state);
      state.windowOffsetHours = clamp(state.windowOffsetHours, 0, win.maxOffsetHours);
      persist();
      const obsN = series.observed.length;
      const predN = series.predicted.length;
      setStatus(`Loaded ${obsN} observed and ${predN} predicted points.`);
    } catch (err) {
      console.error("[Tides] load series failed", err);
      setStatus(`Failed to load data: ${err?.message || String(err)}`);
    } finally {
      loading = false;
      render();
    }
  }

  function setPointFromMapCenter() {
    const center = app?.map?.instance?.getCenter?.();
    if (!center) return;
    state.lat = Number(center.lat.toFixed(6));
    state.lon = Number(center.lng.toFixed(6));
    persist();
    updateMapMarkers();
    render();
  }

  function setMapClickMode(enabled) {
    const map = app?.map?.instance;
    if (!map) return;
    if (mapClickOff) {
      try { mapClickOff(); } catch {}
      mapClickOff = null;
    }
    state.autoPickMode = !!enabled;
    persist();
    if (!enabled) return;
    const onClick = (e) => {
      state.lat = Number(e.latlng.lat.toFixed(6));
      state.lon = Number(e.latlng.lng.toFixed(6));
      persist();
      updateMapMarkers();
      render();
      resolveNearestStation();
    };
    map.on("click", onClick);
    mapClickOff = () => map.off("click", onClick);
  }

  function renderHiLoTable() {
    const win = windowParams(state);
    const filtered = (series.hilo || []).filter((p) => Number.isFinite(p?.t) && p.t >= win.startMs && p.t <= win.endMs);
    if (!filtered.length) {
      return `<div class="small">No high/low events in selected window.</div>`;
    }
    const rows = filtered.slice(0, 10).map((r) => `
      <tr>
        <td>${esc(r.type === "H" ? "High" : "Low")}</td>
        <td>${esc(fmtTime(r.t))}</td>
        <td>${esc(formatValue(r.v, state.units))}</td>
      </tr>
    `).join("");
    return `
      <table style="width:100%; border-collapse:collapse; margin-top:6px;">
        <thead>
          <tr>
            <th style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.25);">Type</th>
            <th style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.25);">Time (UTC)</th>
            <th style="text-align:right; border-bottom:1px solid rgba(148,163,184,0.25);">Level</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  function renderRawDataTable() {
    const rows = [
      ...(series.observed || []).map((r) => ({ source: "Observed", type: "Water Level", t: r.t, v: r.v })),
      ...(series.predicted || []).map((r) => ({ source: "Predicted", type: "Water Level", t: r.t, v: r.v })),
      ...(series.hilo || []).map((r) => ({ source: "Predicted", type: r.type === "H" ? "High" : "Low", t: r.t, v: r.v })),
    ]
      .filter((r) => Number.isFinite(r?.t))
      .sort((a, b) => a.t - b.t);
    if (!rows.length) return `<div class="small">No raw tide rows loaded.</div>`;
    const body = rows.slice(0, 1000).map((r) => `
      <tr>
        <td>${esc(r.source)}</td>
        <td>${esc(r.type)}</td>
        <td>${esc(fmtTime(r.t))}</td>
        <td style="text-align:right;">${esc(formatValue(r.v, state.units))}</td>
      </tr>
    `).join("");
    return `
      <div class="small" style="margin-bottom:6px;">Showing ${Math.min(rows.length, 1000)} of ${rows.length} rows.</div>
      <div style="overflow:auto; max-height:100%;">
        <table style="width:100%; border-collapse:collapse;">
          <thead>
            <tr>
              <th style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.25);">Source</th>
              <th style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.25);">Type</th>
              <th style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.25);">Time (UTC)</th>
              <th style="text-align:right; border-bottom:1px solid rgba(148,163,184,0.25);">Level</th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    `;
  }

  function computeWindowView() {
    const win = windowParams(state);
    const observedWin = filterSeriesByWindow(series.observed, win.startMs, win.endMs);
    const predictedWin = filterSeriesByWindow(series.predicted, win.startMs, win.endMs);
    const chart = renderSeriesSvg(observedWin, predictedWin, hoverX);
    const hoverText = chart.hover
      ? `Hover ${fmtTime(chart.hover.t)} · Obs ${formatValue(chart.hover.observed?.v, state.units)} · Pred ${formatValue(chart.hover.predicted?.v, state.units)}`
      : "Hover chart for values";
    return { win, chart, hoverText };
  }

  function renderBottomPanelHtml() {
    const { win, chart, hoverText } = computeWindowView();
    const activeTab = ["graph", "table", "raw"].includes(state.bottomTab) ? state.bottomTab : "graph";
    const tabBtn = (id, label) => `<button type="button" class="btn${activeTab === id ? " primary" : ""}" data-tides-tab="${id}">${label}</button>`;
    return `
      <div class="card" id="tides-bottom-card" style="height:100%; display:flex; flex-direction:column; min-height:0; padding:10px;">
        <div class="row" style="gap:8px; align-items:center; flex-wrap:wrap;">
          <div style="font-weight:700;">Tides</div>
          <div class="small" style="margin-left:auto;">Station ${esc(state.stationId || "--")} · ${esc(state.stationName || "--")}</div>
        </div>
        <div class="row" style="gap:8px; margin-top:8px; flex-wrap:wrap;">
          ${tabBtn("graph", "Graph")}
          ${tabBtn("table", "Table")}
          ${tabBtn("raw", "Raw Data")}
        </div>
        <div style="margin-top:8px; flex:1; min-height:0; display:flex; flex-direction:column;">
          <div id="tides-tab-graph" style="display:${activeTab === "graph" ? "flex" : "none"}; flex-direction:column; flex:1; min-height:0;">
            <div class="small">Observed vs Predicted (48-hour window)</div>
            <div id="tides-window-range" class="small" style="margin:2px 0 6px;">${esc(fmtTime(win.startMs))} to ${esc(fmtTime(win.endMs))}</div>
            <div id="tides-chart-slot">${chart.html}</div>
            <div id="tides-hover-readout" class="small" style="margin-top:6px;">${esc(hoverText)}</div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <button id="tides-window-prev" class="btn">-6h</button>
              <button id="tides-window-next" class="btn">+6h</button>
              <span id="tides-window-progress-label" class="small" style="margin-left:auto;">offset ${Math.round(win.offsetHours)}h / ${Math.round(win.maxOffsetHours)}h</span>
            </div>
            <div class="row lm-add-row" style="margin-top:8px; align-items:center;">
              <label class="small" style="min-width:72px;">Window</label>
              <div id="tides-window-scrub"
                data-max="${Math.max(0, win.maxOffsetHours)}"
                style="position:relative; flex:1; height:18px; cursor:pointer; user-select:none; touch-action:none;">
                <div style="position:absolute; left:0; right:0; top:7px; height:4px; border-radius:999px; background:rgba(31,41,55,1);"></div>
                <div id="tides-window-scrub-fill" style="position:absolute; left:0; top:7px; height:4px; border-radius:999px; background:rgba(56,189,248,0.8); width:${Math.max(0, Math.min(100, win.maxOffsetHours > 0 ? (win.offsetHours / win.maxOffsetHours) * 100 : 0)).toFixed(2)}%;"></div>
                <div id="tides-window-scrub-thumb" style="position:absolute; top:3px; width:12px; height:12px; border-radius:999px; background:#38bdf8; border:1px solid rgba(15,23,42,1); box-shadow:0 0 0 4px rgba(56,189,248,.12); left:calc(${Math.max(0, Math.min(100, win.maxOffsetHours > 0 ? (win.offsetHours / win.maxOffsetHours) * 100 : 0)).toFixed(2)}% - 6px);"></div>
              </div>
              <span id="tides-window-offset-label" class="small" style="min-width:42px; text-align:right;">+${Math.round(win.offsetHours)}h</span>
            </div>
          </div>
          <div id="tides-tab-table" style="display:${activeTab === "table" ? "block" : "none"}; flex:1; min-height:0; overflow:auto;">
            <div class="small">Upcoming High/Low (windowed)</div>
            <div id="tides-hilo-slot">${renderHiLoTable()}</div>
          </div>
          <div id="tides-tab-raw" style="display:${activeTab === "raw" ? "block" : "none"}; flex:1; min-height:0; overflow:auto;">
            <div class="small">Raw loaded tide rows</div>
            <div id="tides-raw-slot" style="margin-top:6px; height:calc(100% - 20px);">${renderRawDataTable()}</div>
          </div>
        </div>
      </div>
    `;
  }

  function updateWindowPanels() {
    const { win, chart, hoverText } = computeWindowView();
    const root = document.getElementById("tides-bottom-card");
    if (!root) return;
    const rangeEl = root.querySelector("#tides-window-range");
    const chartEl = root.querySelector("#tides-chart-slot");
    const hoverEl = root.querySelector("#tides-hover-readout");
    const hiloEl = root.querySelector("#tides-hilo-slot");
    const rawEl = root.querySelector("#tides-raw-slot");
    const progressEl = root.querySelector("#tides-window-progress-label");
    const scrubEl = root.querySelector("#tides-window-scrub");
    if (rangeEl) rangeEl.textContent = `${fmtTime(win.startMs)} to ${fmtTime(win.endMs)}`;
    if (chartEl) chartEl.innerHTML = chart.html;
    if (hoverEl) hoverEl.textContent = hoverText;
    if (hiloEl) hiloEl.innerHTML = renderHiLoTable();
    if (rawEl) rawEl.innerHTML = renderRawDataTable();
    if (progressEl) progressEl.textContent = `offset ${Math.round(win.offsetHours)}h / ${Math.round(win.maxOffsetHours)}h`;
    if (scrubEl) scrubEl.dataset.max = String(Math.max(0, win.maxOffsetHours));
    updateWindowScrubUi();
  }

  function render() {
    if (!containerEl) return;
    const distanceText = Number.isFinite(nearestInfo.distanceKm)
      ? formatDistanceKm(nearestInfo.distanceKm)
      : "--";

    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">Tides (NOAA CO-OPS)</div>
        <div class="small" style="margin-top:6px;">
          Realtime/historic water levels and tide predictions with nearest-station lookup.
        </div>

        <div class="lm-title" style="margin-top:10px;">Station Pick</div>
        <div class="row lm-add-row">
          <label class="small" style="min-width:48px;">Lat</label>
          <input id="tides-lat" type="number" step="0.0001" value="${esc(state.lat)}" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:48px;">Lon</label>
          <input id="tides-lon" type="number" step="0.0001" value="${esc(state.lon)}" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <button id="tides-map-center" class="btn">Use Map Center</button>
          <button id="tides-nearest" class="btn primary" ${loading ? "disabled" : ""}>Find Nearest</button>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="tides-map-pick" type="checkbox" ${state.autoPickMode ? "checked" : ""} />
            Map click pick
          </label>
        </div>
        <div class="small" style="margin-top:6px;">
          Station: <strong>${esc(state.stationName || "--")}</strong> (${esc(state.stationId || "--")}) · distance ${esc(distanceText)}
        </div>

        <div class="lm-title" style="margin-top:10px;">Data Range</div>
        <div class="row lm-add-row">
          <label class="small" style="min-width:48px;">Start</label>
          <input id="tides-start" type="datetime-local" value="${esc(toLocalInputValue(new Date(state.startIso).getTime()))}" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:48px;">End</label>
          <input id="tides-end" type="datetime-local" value="${esc(toLocalInputValue(new Date(state.endIso).getTime()))}" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <select id="tides-datum">
            <option value="MLLW"${state.datum === "MLLW" ? " selected" : ""}>MLLW</option>
            <option value="MSL"${state.datum === "MSL" ? " selected" : ""}>MSL</option>
            <option value="NAVD"${state.datum === "NAVD" ? " selected" : ""}>NAVD</option>
            <option value="STND"${state.datum === "STND" ? " selected" : ""}>Station Datum</option>
          </select>
          <select id="tides-units">
            <option value="metric"${state.units === "metric" ? " selected" : ""}>Metric (m)</option>
            <option value="english"${state.units === "english" ? " selected" : ""}>English (ft)</option>
          </select>
          <button id="tides-load" class="btn primary" ${loading ? "disabled" : ""}>Load Series</button>
        </div>

        <div id="tides-workspace-host" style="margin-top:10px;">
          ${renderBottomPanelHtml()}
        </div>

        <div class="small" style="margin-top:10px; min-height:16px;">${esc(status)}</div>
      </div>
    `;
    bindBottomPanelControls();
  }

  function onClick(e) {
    if (e.target?.closest?.("#tides-map-center")) {
      setPointFromMapCenter();
      return;
    }
    if (e.target?.closest?.("#tides-nearest")) {
      resolveNearestStation();
      return;
    }
    if (e.target?.closest?.("#tides-load")) {
      loadSeries();
    }
  }

  function onInput(e) {
    const id = e.target?.id;
    if (!id) return;
  }

  function updateWindowScrubUi() {
    const root = document.getElementById("tides-bottom-card");
    const scrub = root?.querySelector?.("#tides-window-scrub");
    const fill = root?.querySelector?.("#tides-window-scrub-fill");
    const thumb = root?.querySelector?.("#tides-window-scrub-thumb");
    const lbl = root?.querySelector?.("#tides-window-offset-label");
    if (!scrub || !fill || !thumb) return;
    const max = Math.max(0, Number(scrub.dataset.max) || 0);
    const pct = max > 0 ? (Math.max(0, Math.min(max, Number(state.windowOffsetHours) || 0)) / max) * 100 : 0;
    fill.style.width = `${pct}%`;
    thumb.style.left = `calc(${pct}% - 6px)`;
    if (lbl) lbl.textContent = `+${Math.round(state.windowOffsetHours)}h`;
  }

  function setOffsetFromScrubEvent(ev) {
    const scrub = document.getElementById("tides-window-scrub");
    if (!scrub) return;
    const rect = scrub.getBoundingClientRect();
    const max = Math.max(0, Number(scrub.dataset.max) || 0);
    const px = clamp((ev.clientX ?? 0) - rect.left, 0, rect.width || 1);
    const frac = rect.width > 0 ? px / rect.width : 0;
    state.windowOffsetHours = clamp(Math.round(frac * max), 0, max);
    persist();
    updateWindowScrubUi();
  }

  function onPointerDown(e) {
    const scrub = e.target?.closest?.("#tides-window-scrub");
    if (!scrub) return;
    scrubDragging = true;
    try { scrub.setPointerCapture?.(e.pointerId); } catch {}
    setOffsetFromScrubEvent(e);
    hoverX = null;
    updateWindowPanels();
    e.preventDefault();
  }

  function onPointerMove(e) {
    if (!scrubDragging) return;
    setOffsetFromScrubEvent(e);
    hoverX = null;
    updateWindowPanels();
    e.preventDefault();
  }

  function onPointerUp(e) {
    if (!scrubDragging) return;
    scrubDragging = false;
    setOffsetFromScrubEvent(e);
    updateWindowPanels();
  }

  function onMouseMove(e) {
    if (scrubDragging) return;
    const svg = e.target?.closest?.("#tides-chart-svg");
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (!Number.isFinite(x)) return;
    const viewW = 780;
    hoverX = clamp((x / Math.max(1, rect.width)) * viewW, 0, viewW);
    updateWindowPanels();
  }

  function onMouseLeave(e) {
    if (scrubDragging) return;
    const wrap = e.target?.closest?.("#tides-chart-wrap");
    if (!wrap) return;
    if (wrap.contains(e.relatedTarget)) return;
    hoverX = null;
    updateWindowPanels();
  }

  function onChange(e) {
    const id = e.target?.id;
    if (!id) return;
    if (id === "tides-map-pick") {
      setMapClickMode(!!e.target.checked);
      render();
      return;
    }
    parseForm();
  }

  function bindBottomPanelControls() {
    const root = document.getElementById("tides-bottom-card");
    if (!root) return;
    root.onclick = (e) => {
      const tab = e.target?.closest?.("[data-tides-tab]")?.getAttribute?.("data-tides-tab");
      if (tab) {
        state.bottomTab = ["graph", "table", "raw"].includes(tab) ? tab : "graph";
        persist();
        renderBottomPanelOnly();
        return;
      }
      const win = windowParams(state);
      if (e.target?.closest?.("#tides-window-prev")) {
        state.windowOffsetHours = clamp(Math.round(win.offsetHours) - 6, 0, win.maxOffsetHours);
        persist();
        updateWindowPanels();
        return;
      }
      if (e.target?.closest?.("#tides-window-next")) {
        state.windowOffsetHours = clamp(Math.round(win.offsetHours) + 6, 0, win.maxOffsetHours);
        persist();
        updateWindowPanels();
      }
    };
    root.onpointerdown = onPointerDown;
    root.onpointermove = onPointerMove;
    root.onpointerup = onPointerUp;
    root.onpointercancel = onPointerUp;
    root.onmousemove = onMouseMove;
    root.onmouseout = onMouseLeave;
  }

  function renderBottomPanelOnly() {
    const host = containerEl?.querySelector?.("#tides-workspace-host");
    if (!host) return;
    host.innerHTML = renderBottomPanelHtml();
    bindBottomPanelControls();
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onChange);
    }
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onChange);
    updateMapMarkers();
    setMapClickMode(state.autoPickMode);
    render();
    if (!series.observed.length && !series.predicted.length && state.stationId) {
      loadSeries();
    }
  }

  function destroy() {
    if (mapClickOff) {
      try { mapClickOff(); } catch {}
      mapClickOff = null;
    }
    if (stationMarker && app.map.instance.hasLayer(stationMarker)) app.map.instance.removeLayer(stationMarker);
    if (pickMarker && app.map.instance.hasLayer(pickMarker)) app.map.instance.removeLayer(pickMarker);
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("change", onChange);
    containerEl = null;
  }

  function snapshot() {
    return {
      version: 1,
      state: {
        ...state,
      },
    };
  }

  function restore(data) {
    const s = data?.state;
    if (!s || typeof s !== "object") return;
    state = {
      ...state,
      ...s,
      autoPickMode: !!s.autoPickMode,
      windowOffsetHours: Math.max(0, asNum(s.windowOffsetHours, 0)),
      bottomTab: ["graph", "table", "raw"].includes(s.bottomTab) ? s.bottomTab : "graph",
    };
    persist();
    updateMapMarkers();
    setMapClickMode(state.autoPickMode);
    render();
  }

  return { mount, destroy, snapshot, restore };
}

